'use client'

import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  GliderBackdrop,
} from '@/components/award'

export interface LegalArticle {
  num: string          // Anzeige-Praefix wie "§ 1" oder "(1)" — wird in der TOC + Eyebrow verwendet
  numDisplay: string   // 2-stelliger Display-Wert wie "01", "14a" — wird als Riesen-Display-Nummer und im scroll-Anker verwendet
  title: string
  /**
   * Optionaler i18n-Key fuer die Uebersetzung des Artikel-Titels.
   * Wenn gesetzt und aktive Locale != 'de' wird die Uebersetzung
   * in Akzentfarbe unter dem deutschen Original eingeblendet.
   * Der deutsche Text bleibt rechtsverbindlich.
   */
  titleI18nKey?: string
  body: React.ReactNode
  /**
   * Optionaler i18n-Key fuer eine 3-4-Satz-Zusammenfassung des Artikel-Bodys.
   * Wenn gesetzt und aktive Locale != 'de' wird die Zusammenfassung
   * in einer eigenen Box UNTER dem deutschen Original eingeblendet
   * (informativ, nicht rechtlich bindend).
   * DE-Original bleibt allein rechtsverbindlich.
   */
  summaryI18nKey?: string
}

export interface LegalHeroChip {
  label: string
}

export interface LegalShellProps {
  /** Eyebrow oben links neben der ersten Trennlinie */
  eyebrow: string
  /** Optional: Label rechts der Trennlinie (Saison, Stand, etc.) */
  meta?: string
  /** H1-Headline. Mit `{grad}` wird das Wort als Gold-Gradient hervorgehoben (per gradWord) */
  title: string
  /** Wort innerhalb des title das als award-grad-text dargestellt wird */
  gradWord?: string
  /** Lead-Paragraph rechts neben dem Titel */
  lead: string
  /** Bis 4 Award-Chips fuer die Hero-Meta */
  chips?: LegalHeroChip[]
  /** Optionaler Hinweis (z.B. generisches Maskulinum) — wird unter den Chips gerendert */
  notice?: string
  /** Inhalts-Eyebrow oben in der TOC */
  tocEyebrow?: string
  /** Inhalts-Heading in der TOC */
  tocTitle?: string
  /** Die Paragraphen / Artikel der Seite */
  articles: LegalArticle[]
  /** Schlusssektion: Eyebrow */
  closingEyebrow?: string
  /** Schlusssektion: Heading (kann <Link>-Markup enthalten) */
  closingTitle?: React.ReactNode
  /** Primaerer CTA-Link unten (Default: /register, "Registrieren") */
  primaryCta?: { href: string; label: string }
  /** Sekundaerer CTA-Link unten (Default: "/", "Zurueck zur Startseite") */
  secondaryCta?: { href: string; label: string }
  /** Wenn true: GliderOrbit im Hero zeigen (Default true) */
  showGlider?: boolean
}

/**
 * Wiederverwendbares Award-Layout fuer Legal- und Editorial-Seiten.
 * - LenisProvider Wrapper fuer Smooth-Scroll
 * - LivingThermal + GliderOrbit + award-noise im Hero
 * - Auto-generierte TOC mit Smooth-Scroll-Buttons
 * - Editorial-Grid pro Artikel mit Riesen-Display-§-Nummer + sticky
 * - Reveal-Animationen kaskadiert
 * - Thermal-Divider zwischen Artikeln
 * - Closing-Section mit Magnetic-Buttons + animiertem Hintergrund
 */
export function AwardLegalShell({
  eyebrow,
  meta,
  title,
  gradWord,
  lead,
  chips = [],
  notice,
  tocEyebrow,
  tocTitle,
  articles,
  closingEyebrow,
  closingTitle,
  primaryCta,
  secondaryCta,
  showGlider = true,  // eslint-disable-line @typescript-eslint/no-unused-vars
}: LegalShellProps) {
  const { locale, t } = useTranslation()
  const isDe = locale === 'de'
  // R102.8 (i18n-Sprint 2026-06-02): Defaults aus legal.shell.* statt
  // hardcoded DE. Pages dürfen weiterhin per Prop überschreiben.
  const tocEyebrowFinal = tocEyebrow ?? t('legal.shell.tocEyebrow')
  const tocTitleFinal = tocTitle ?? t('legal.shell.tocTitle')
  const entriesUnit = t('legal.shell.entriesUnit')
  const tapScrollLabel = t('legal.shell.tapScroll')
  const primaryCtaFinal = primaryCta ?? {
    href: '/register',
    label: t('legal.shell.primaryCtaLabel'),
  }
  const secondaryCtaFinal = secondaryCta ?? {
    href: '/',
    label: t('legal.shell.secondaryCtaLabel'),
  }
  // Atmosphaere + Segler-Toggle laufen jetzt komplett ueber <GliderBackdrop>
  // (gleicher Stil + gleicher Toggle wie auf Home, Pricing, FAQ, Weather).
  // Damit fliegen die Segler durch den sichtbaren Viewport (inkl. Header-Bereich),
  // statt sich auf die volle Page-Hoehe zu verteilen — Bug-Fix 2026-05-09.
  // Der vorige eigene Atmosphaere-Layer + lokaler Toggle wurde entfernt.

  const scrollToParagraph = (numDisplay: string) => {
    const el = document.getElementById(`art-${numDisplay}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const renderTitle = () => {
    if (!gradWord || !title.includes(gradWord)) {
      return <h1 className="award-h-mega">{title}</h1>
    }
    const parts = title.split(gradWord)
    return (
      <h1 className="award-h-mega">
        {parts[0]}
        <span className="award-grad-text">{gradWord}</span>
        {parts[1]}
      </h1>
    )
  }

  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen overflow-x-clip" style={{ background: 'var(--award-ink)' }}>
        <FglHeader />
        {/* R100.57 (2026-05-30): GliderBackdrop auf Legal-Pages entfernt — die
            Canvas-Animation hat zusammen mit Lenis-Smooth-Scroll + 79+ Reveal-
            IntersectionObservern messbares Scroll-Lag auf der Datenschutz-Seite
            erzeugt (1500+ DOM-Nodes). Atmosphäre bleibt durch die übrigen
            Pages erhalten — Rechtsseiten sind Text-only und brauchen die
            animierten Segler nicht. */}

        {/* HERO */}
        <section className="relative pt-[130px] pb-[80px] md:pt-[180px] md:pb-[110px] overflow-hidden">

          <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="award-eyebrow">{eyebrow}</span>
                {meta && (
                  <>
                    <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                    <span className="award-label">{meta}</span>
                  </>
                )}
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-6 md:gap-12 items-end">
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>{renderTitle()}</Reveal>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  <p className="award-body-lead">{lead}</p>
                </Reveal>
                {chips.length > 0 && (
                  <Reveal delay={3}>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {chips.map((c) => (
                        <span key={c.label} className="award-chip">{c.label}</span>
                      ))}
                    </div>
                  </Reveal>
                )}
                {notice && (
                  <Reveal delay={4}>
                    {/* R107.16.2: opacity 0.85 raus (Triple-Stack mit cirrus-3 unleserlich) */}
                    <p className="text-xs italic mt-6 max-w-[44ch]" style={{ color: 'var(--award-cirrus-3)' }}>
                      {notice}
                    </p>
                  </Reveal>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* MEHRSPRACHIGER HINWEIS — nur wenn aktive Locale != DE */}
        {!isDe && (
          <section className="relative pt-4 pb-2">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12">
              <div className="award-legal-notice">
                <span className="award-legal-notice-flag" aria-hidden>§</span>
                <div className="award-legal-notice-body">
                  <div className="award-legal-notice-de">
                    Rechtlich verbindlich ist allein die deutsche Originalfassung. Die Artikel-Überschriften zeigen wir zusätzlich in deiner Sprache zur Orientierung.
                  </div>
                  <div className="award-legal-notice-tr">
                    {t('legal.notice.localized')}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* TOC */}
        <section
          className="relative py-14 md:py-20 border-y"
          style={{ background: 'var(--award-ink-2)', borderColor: 'var(--award-line)' }}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-end justify-between mb-8">
                <div>
                  <div className="award-eyebrow mb-2">{tocEyebrowFinal} · {articles.length} {entriesUnit}</div>
                  <h2 className="award-h-md">{tocTitleFinal}</h2>
                </div>
                <span className="award-label hidden md:block">{tapScrollLabel}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {articles.map((p, i) => {
                const translatedTitle =
                  !isDe && p.titleI18nKey ? t(p.titleI18nKey) : null
                const hasTranslation =
                  translatedTitle && translatedTitle !== p.titleI18nKey && translatedTitle !== p.title
                return (
                  <Reveal key={p.num + p.numDisplay} delay={(i % 5) as 0 | 1 | 2 | 3 | 4} as="div">
                    <button
                      type="button"
                      onClick={() => scrollToParagraph(p.numDisplay)}
                      className="award-toc-item"
                    >
                      <span className="award-toc-num">{p.numDisplay}</span>
                      <span className="award-toc-title">{p.title}</span>
                      {hasTranslation && (
                        <span className="award-toc-title-translation">{translatedTitle}</span>
                      )}
                    </button>
                  </Reveal>
                )
              })}
            </div>
          </div>
        </section>

        {/* ARTICLES */}
        <section className="pt-16 md:pt-24 pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="space-y-16 md:space-y-24">
              {articles.map((p, i) => (
                <Reveal key={p.num + p.numDisplay} as="article" id={`art-${p.numDisplay}`} className="scroll-mt-24">
                  <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-3">
                      <div className="md:sticky md:top-[110px]">
                        <div className="award-paragraph-num">{p.numDisplay}</div>
                        <div className="award-eyebrow">{p.num}</div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                      <h3 className="award-h-lg mb-2 max-w-[28ch]">
                        {p.title}.
                      </h3>
                      {!isDe && p.titleI18nKey && (() => {
                        const tr = t(p.titleI18nKey)
                        if (!tr || tr === p.titleI18nKey || tr === p.title) return null
                        return (
                          <h4 className="award-h-translation mb-6 max-w-[32ch]">
                            {tr}
                          </h4>
                        )
                      })()}
                      <div className="award-prose">{p.body}</div>
                      {!isDe && p.summaryI18nKey && (() => {
                        const summary = t(p.summaryI18nKey)
                        if (!summary || summary === p.summaryI18nKey) return null
                        return (
                          <div className="award-legal-summary" style={{ marginTop: '1.5rem' }}>
                            <div className="award-legal-summary-header">
                              <span className="award-legal-summary-flag" aria-hidden>i</span>
                              <span className="award-legal-summary-label">
                                {t('legal.shell.summaryLabel')}
                              </span>
                            </div>
                            <p className="award-legal-summary-body">{summary}</p>
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                  {i < articles.length - 1 && (
                    <div className="award-thermal-divider mt-16 md:mt-24" aria-hidden />
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING */}
        <section
          className="py-20 md:py-28 border-t relative overflow-hidden"
          style={{ borderColor: 'var(--award-line)', background: 'var(--award-ink-2)' }}
        >
          <div className="award-thermal-dot" style={{ left: '12%', top: '20%' }} />
          <div className="award-thermal-dot" style={{ left: '88%', top: '70%', animationDelay: '1.5s' }} />
          <div className="max-w-[1100px] mx-auto px-6 text-center relative z-10">
            {closingEyebrow && (
              <Reveal>
                <div className="award-eyebrow mb-4">{closingEyebrow}</div>
              </Reveal>
            )}
            {closingTitle && (
              <Reveal delay={1}>
                <h3 className="award-h-lg mb-8">{closingTitle}</h3>
              </Reveal>
            )}
            <Reveal delay={2}>
              <div className="flex gap-4 justify-center flex-wrap">
                <Magnetic>
                  <Link href={primaryCtaFinal.href} className="award-btn award-btn-primary">
                    {primaryCtaFinal.label} <span aria-hidden>→</span>
                  </Link>
                </Magnetic>
                <Magnetic>
                  <Link href={secondaryCtaFinal.href} className="award-btn award-btn-ghost">
                    {secondaryCtaFinal.label}
                  </Link>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Segler-Toggle „Segler an/aus" wird jetzt von <GliderBackdrop> oben
            global gerendert — kein eigener Toggle mehr noetig. */}
      </div>
    </LenisProvider>
  )
}

/* ─────────────────────────────────────────────────────────────────────
   HELPER-Komponenten fuer den Body von Artikeln
   ───────────────────────────────────────────────────────────────────── */

/** Sub-Artikel innerhalb eines Paragraphen (z.B. § 14a, § 14b) */
export function AwardSubArticle({
  num,
  title,
  children,
}: {
  num: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="award-sub-article">
      <div className="award-sub-meta">
        <span className="award-sub-num">{num}</span>
        <span className="award-sub-rule" aria-hidden />
      </div>
      <h4 className="award-sub-title">{title}</h4>
      <div className="award-sub-body">{children}</div>
    </div>
  )
}

/** Daten-Kategorie fuer Datenschutz § 4 (1)(2)(3)... */
export function AwardDataCategory({
  num,
  title,
  children,
}: {
  num: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="award-data-category">
      <div className="award-data-category-header">
        <span className="award-data-category-num">{num}</span>
        <span className="award-data-category-title">{title}</span>
      </div>
      <div className="award-data-category-body">{children}</div>
    </div>
  )
}

/** Pflichten-Liste mit Mono-Gold-Nummern */
export function AwardPflichtList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="award-pflicht-list">
      {items.map((item, i) => (
        <li key={i}>
          <span className="award-pflicht-num">{String(i + 1).padStart(2, '0')}</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

/** Laender-Grid fuer internationale Listen */
export function AwardLaenderGrid({
  items,
}: {
  items: { code: string; name: string; text: React.ReactNode }[]
}) {
  return (
    <div className="award-laender-grid">
      {items.map((r) => (
        <div key={r.code + r.name} className="award-land">
          <div className="award-land-code">{r.code}</div>
          <div>
            <div className="award-land-name">{r.name}</div>
            <div className="award-land-text">{r.text}</div>
          </div>
        </div>
      ))}
    </div>
  )
}
